import { useQueryClient } from '@tanstack/react-query';
import { Scale, SquarePen } from 'lucide-react';
import { useCallback, useState, type ReactElement } from 'react';
import { useNavigate } from 'react-router';
import {
  useGetBuyerInventories,
  useUpsertBuyerInventoryConfiguration,
  useUpsertInventory,
} from '~/api';
import { Modal } from '~/components/overlays';
import {
  Table,
  useTable,
  type TableColumn,
  type TableRow,
} from '~/components/ui/table';
import type { BuyerInventoryQuery, Inventory } from '~/graphql/graphql';
import { usePaginatedQuery } from '~/hooks/usePaginatedQuery';
import { getInventoryStatus } from '~/utils/inventory';
import { EditInventoryModal } from './EditInventoryModal';
import type { PaginatedResponse } from '~/types';

function getActionButtons(
  inv: Inventory,
  onOpenEdit: (inv: Inventory) => void,
  t: (key: string) => string,
  idx: number,
  navigate: (path: string) => void
): ReactElement {
  return (
    <div className="flex items-center justify-end gap-2">
      <button
        data-testid={`edit-inv-${idx}`}
        className="p-1.5 text-neut-600 hover:text-prim-500 hover:bg-neut-100 rounded transition-colors"
        onClick={() => onOpenEdit(inv)}
        title={t('inventory.edit')}
      >
        <SquarePen size={16} aria-hidden="true" />
      </button>
      <button
        data-testid={`view-inv-${idx}`}
        className="p-1.5 text-neut-600 hover:text-prim-500 hover:bg-neut-100 rounded transition-colors"
        title={t('inventory.view')}
        onClick={() =>
          navigate(
            `/order-builder/view?inventoryId=${inv.id}&platformProductLengthId=${inv.platformProductsLength.id}&locationId=${inv.location.id}&platformProductId=${inv.platformProductsLength.platformProduct.id}&returnPath=${window.location.pathname}`
          )
        }
      >
        <Scale size={16} aria-hidden="true" />
      </button>
    </div>
  );
}

function getWeeksCovered(
  weeksCovered: string | number,
  weeksCoveredBeforeUrgent: number,
  weeksCoveredBeforeMonitor: number
): ReactElement {
  let numeric: number;
  if (typeof weeksCovered === 'number') {
    numeric = weeksCovered;
  } else if (weeksCovered === 'Infinity') {
    numeric = Infinity;
  } else {
    numeric = parseFloat(weeksCovered);
  }

  if (!Number.isFinite(numeric)) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
        Infinity
      </span>
    );
  }

  const label = `${numeric.toFixed(2)} weeks`;
  if (numeric <= weeksCoveredBeforeUrgent) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-error-700 bg-error-100">
        {label}
      </span>
    );
  }

  if (numeric <= weeksCoveredBeforeMonitor) {
    return (
      <span className="px-2 py-1 rounded-full text-xs font-medium text-warning-700 bg-warning-100">
        {label}
      </span>
    );
  }

  return (
    <span className="px-2 py-1 rounded-full text-xs font-medium bg-success-100 text-success-700">
      {label}
    </span>
  );
}

type InventoryTableProps = { readonly t: (key: string) => string };

export function InventoryTable({ t }: Readonly<InventoryTableProps>) {
  const [showEditInventory, setShowEditInventory] = useState(false);
  const [editingInventory, setEditingInventory] = useState<Inventory | null>(
    null
  );

  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const upsertInventory = useUpsertInventory();
  const upsertInventoryConfiguration = useUpsertBuyerInventoryConfiguration();

  const {
    data: allInventories,
    sentinelRef,
    isLoadingMore,
    isLoading,
    hasMoreData,
    error,
    reset,
    updateItem,
    pagination,
  } = usePaginatedQuery<BuyerInventoryQuery, Inventory>({
    fetchHook: useGetBuyerInventories,
    select: (data) => data?.buyerInventory as PaginatedResponse<Inventory>,
  });

  const onSuccessRefresh = useCallback(() => {
    reset();
    void (async () => {
      try {
        await queryClient.invalidateQueries({ queryKey: ['buyerInventories'] });
      } catch {
        // ignore
      }
    })();
  }, [queryClient, reset]);

  const handleTableEditSubmit = (
    rowId: string,
    columnKey: string,
    newValue: string | number
  ): void => {
    const inventory = allInventories.find((i) => i.id === rowId);
    if (!inventory) return;

    const platformProductsLengthId = inventory.platformProductsLength.id;
    const locationId = inventory.location.id;
    if (!platformProductsLengthId || !locationId) return;

    if (columnKey === 'stock') {
      upsertInventory.mutate(
        {
          platformProductsLengthId,
          locationId,
          quantity: {
            value: Number(newValue),
            unit: 'MBF',
          },
        },
        {
          onSuccess: (result) => {
            const newQuantity = result?.upsertInventory?.quantity;
            updateItem(rowId, (item) => ({
              ...item,
              quantity: newQuantity ?? {
                value: Number(newValue),
                unit: 'MBF' as const,
              },
            }));
            tableControls?.handleEditDone?.();
          },
        }
      );
      return;
    }

    if (columnKey === 'usageRatePerWeek') {
      upsertInventoryConfiguration.mutate(
        {
          platformProductsLengthId,
          locationId,
          usageRatePerWeek: {
            value: Number(newValue),
            unit: 'MBF',
          },
        },
        {
          onSuccess: () => {
            updateItem(rowId, (item) => ({
              ...item,
              buyerInventoryConfiguration: item.buyerInventoryConfiguration
                ? {
                    ...item.buyerInventoryConfiguration,
                    usageRatePerWeek: {
                      value: Number(newValue),
                      unit: 'MBF' as const,
                    },
                  }
                : item.buyerInventoryConfiguration,
            }));
            tableControls?.handleEditDone?.();
          },
        }
      );
    }
  };

  const tableControls = useTable({
    onSort: () => {},
    onEditSubmit: handleTableEditSubmit,
  });

  const columns: TableColumn[] = [
    {
      key: 'productCode',
      header: t('inventory.product_code'),
      isSortable: true,
    },
    { key: 'product', header: t('inventory.product_name'), isSortable: true },
    { key: 'length', header: t('inventory.length'), isSortable: false },
    { key: 'location', header: t('inventory.location'), isSortable: true },
    {
      key: 'stock',
      header: t('inventory.stock'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'inbound', header: t('inventory.inbound'), isSortable: false },
    { key: 'weeksCovered', header: t('inventory.cover'), isSortable: true },
    {
      key: 'usageRatePerWeek',
      width: 10,
      header: t('inventory.usage'),
      isSortable: true,
      isEditable: true,
      editableType: 'number',
    },
    { key: 'status', header: t('inventory.status'), isSortable: false },
    { key: 'actions', header: t('inventory.actions'), isSortable: false },
  ];

  const openEdit = (inv: Inventory) => {
    setEditingInventory(inv);
    setShowEditInventory(true);
  };

  const rows: TableRow[] = allInventories.map((inv, idx) => {
    const stock = inv.quantity.value ?? 0;
    const weeksCoveredBeforeUrgent =
      inv.buyerInventoryConfiguration?.weeksCoveredBeforeUrgent ?? 0;
    const weeksCoveredBeforeMonitor =
      inv.buyerInventoryConfiguration?.weeksCoveredBeforeMonitor ?? 0;
    const usageRatePerWeek =
      inv.buyerInventoryConfiguration?.usageRatePerWeek.value ?? 0;
    const weeksCovered =
      usageRatePerWeek !== 0 ? (stock / usageRatePerWeek).toFixed(2) : Infinity;
    const productName = inv.platformProductsLength.platformProduct.name;

    return {
      id: { value: inv.id },
      productCode: {
        value: inv.platformProductsLength?.platformProduct?.code ?? '',
      },
      product: { value: productName ?? '' },
      length: { value: inv.platformProductsLength?.length?.displayValue ?? '' },
      location: { value: inv.location?.name ?? '' },
      stock: { value: <span>{stock} MBF</span>, editValue: stock },
      inbound: { value: <span>{inv.inboundStock} MBF</span> },
      weeksCovered: {
        value: getWeeksCovered(
          weeksCovered,
          weeksCoveredBeforeUrgent,
          weeksCoveredBeforeMonitor
        ),
      },
      usageRatePerWeek: { value: <span>{usageRatePerWeek} MBF/week</span> },
      status: {
        value: getInventoryStatus(
          stock,
          usageRatePerWeek,
          weeksCoveredBeforeUrgent,
          weeksCoveredBeforeMonitor,
          t
        ),
      },
      actions: {
        value: getActionButtons(inv, openEdit, t, idx, navigate),
      },
    };
  });

  let tableName = t('inventory.items');
  if (isLoading) tableName = t('inventory.loading');
  else if (error) tableName = t('inventory.load_failed');

  return (
    <div className="overflow-x-auto">
      <Table
        minWidth={60}
        rows={rows}
        columns={columns}
        tableControls={tableControls}
        totalRowsCount={pagination?.totalElements ?? 0}
        name={tableName}
      />
      {isLoadingMore && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">{t('inventory.loading')}</div>
        </div>
      )}
      {hasMoreData === false && !isLoading && (
        <div className="flex justify-center py-4">
          <div className="text-sm text-neut-500">
            {t('inventory.no_more_items')}
          </div>
        </div>
      )}

      <div
        ref={sentinelRef}
        id="scroll-sentinel"
        style={{ height: '1px', background: 'transparent' }}
        data-testid="scroll-sentinel"
      />

      <Modal
        isOpen={showEditInventory}
        onClose={() => {
          setShowEditInventory(false);
          setEditingInventory(null);
        }}
      >
        {editingInventory && (
          <EditInventoryModal
            setShowEditInventory={setShowEditInventory}
            inventory={editingInventory}
            onSaved={onSuccessRefresh}
          />
        )}
      </Modal>
    </div>
  );
}
